import GlobalSearch from '../../../../components/stories/organisms/top-navigation/components/global-search/index.jsx'
import { useEffect, useRef, useState } from 'react'

import { useSelector } from 'react-redux'
import CustomPinsTab from './custom-pins-list.jsx'
import CustomTasksTab from './custom-tasks-list.jsx'
import CustomPlansTab from './custom-plans-list.jsx'
import { useGlobalSearchGetPinsQuery } from '../../../../react-query/global-search/query/use-global-search-get-pins-query.jsx'
import { useGlobalSearchGetTasksQuery } from '../../../../react-query/global-search/query/use-global-search-get-tasks-query.jsx'
import { useGetPlansInfinityQuery } from '../../../../react-query/plan/query/use-get-plans-infinity-query.jsx'
import {
  plansApiTypeOptions,
  planTreeTypeFilterOptions
} from '../../../../react-query/plan/constants.js'
import { useHistory } from 'react-router-dom'
import { makeSelectProjectDetails } from '../../../ProjectSettings/selectors.js'
import { useDebounce } from '../../../../utils/customHooks.js'
import { useCreateFilterMutation } from '../../../../react-query/filters/mutation/use-create-filter-mutation.jsx'
import { pinsSearchFilter, tasksSearchFilter } from './constants.js'

export const SearchBar = () => {
  const history = useHistory()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [counter, setCounter] = useState({
    pins: 0,
    tasks: 0,
    plans: 0
  })

  const projectId = useSelector(makeSelectProjectDetails())?.id
  const filterIdsCache = useRef({})
  const [filterIds, setFilterIds] = useState({
    key: null,
    pins: null,
    tasks: null
  })

  const { mutateAsync: createPinFilter, isPending: isPendingPinFilter } =
    useCreateFilterMutation()

  const { mutateAsync: createTaskFilter, isPending: isPendingTaskFilter } =
    useCreateFilterMutation()

  const trimmedSearch = debouncedSearch.trim()
  const filterKey = `${projectId}:${trimmedSearch}`
  const activeFilterIds = filterIds.key === filterKey ? filterIds : {}
  const isResolvingFilters = !!projectId && filterIds.key !== filterKey

  const tasksQuery = useGlobalSearchGetTasksQuery(
    projectId,
    activeFilterIds.tasks,
    trimmedSearch,
    { enabled: !!activeFilterIds.tasks }
  )
  const pinsQuery = useGlobalSearchGetPinsQuery(
    projectId,
    activeFilterIds.pins,
    trimmedSearch,
    { enabled: !!activeFilterIds.pins }
  )

  const plansQuery = useGetPlansInfinityQuery({
    id: projectId,
    type: plansApiTypeOptions.PROJECTS,
    search: trimmedSearch,
    sortBy: 'LastActivity',
    typeFilter: planTreeTypeFilterOptions.PLANS,
    size: 15,
    stateFilter: 'Active',
    sortDirection: 'DESC'
  })

  useEffect(() => {
    setCounter({
      pins:
        isResolvingFilters || isPendingPinFilter || pinsQuery.isLoading
          ? undefined
          : pinsQuery.data?.pages[0]?.totalElements || 0,
      tasks:
        isResolvingFilters || isPendingTaskFilter || tasksQuery.isLoading
          ? undefined
          : tasksQuery.data?.pages[0]?.totalElements || 0,
      plans: plansQuery.isLoading
        ? undefined
        : plansQuery.data?.pages[0]?.totalElements || 0
    })
  }, [
    isPendingPinFilter,
    isResolvingFilters,
    pinsQuery.data?.pages,
    pinsQuery.isLoading,
    isPendingTaskFilter,
    tasksQuery.data?.pages,
    tasksQuery.isLoading,
    plansQuery.data?.pages,
    plansQuery.isLoading
  ])

  const handleClose = () => setIsOpen(false)

  const handleNavigatePin = (pin) => {
    handleClose()
    history.push(
      `/project/${projectId}/${pin.planReference.planId}/plan/${pin.id}/media`
    )
  }

  const handleNavigateTask = (task) => {
    handleClose()
    history.push(
      `/project/${projectId}/${task.planId}/plan/${task.issueId}/media/task/task-details/${task.id}`
    )
  }

  const handleNavigatePlan = (plan) => {
    handleClose()
    history.push(`/project/${projectId}/${plan.id}/plan`)
  }

  useEffect(() => {
    if (!projectId) return

    let ignore = false

    const getFilterId = (type, createFilter, buildFilter) => {
      const key = `${projectId}:${type}:${trimmedSearch}`

      if (!filterIdsCache.current[key]) {
        filterIdsCache.current[key] = createFilter({
          filter: buildFilter(projectId, trimmedSearch)
        })
          .then(({ id }) => id)
          .catch((error) => {
            delete filterIdsCache.current[key]
            throw error
          })
      }

      return filterIdsCache.current[key]
    }

    Promise.all([
      getFilterId('pins', createPinFilter, pinsSearchFilter),
      getFilterId('tasks', createTaskFilter, tasksSearchFilter)
    ])
      .then(([pins, tasks]) => {
        if (!ignore) {
          setFilterIds({ key: filterKey, pins, tasks })
        }
      })
      .catch(() => {})

    return () => {
      ignore = true
    }
  }, [projectId, trimmedSearch, filterKey, createPinFilter, createTaskFilter])

  return (
    <>
      <GlobalSearch
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        pinsListComponent={
          <CustomPinsTab
            search={search}
            handleNavigatePin={handleNavigatePin}
            pinsQuery={{
              ...pinsQuery,
              isLoading:
                isResolvingFilters || isPendingPinFilter || pinsQuery.isLoading
            }}
          />
        }
        tasksListComponent={
          <CustomTasksTab
            search={search}
            handleNavigateTask={handleNavigateTask}
            tasksQuery={{
              ...tasksQuery,
              isLoading:
                isResolvingFilters ||
                isPendingTaskFilter ||
                tasksQuery.isLoading
            }}
          />
        }
        plansListComponent={
          <CustomPlansTab
            search={search}
            handleNavigatePlan={handleNavigatePlan}
            plansQuery={plansQuery}
          />
        }
        search={search}
        setSearch={setSearch}
        counter={counter}
        disabled={!projectId}
      />
    </>
  )
}
